/**
 * Template Editor Component
 * Comprehensive editor for export templates with tabbed interface
 */

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/InputDefault';
import { TextAreaElement } from '@/components/wizards/wizard-elements/TextAreaElement';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/DefaultSelectDropdown';
import { MultiSelectDropdownWithField, MultiSelectOption } from '@/components/ui/dropdowns/MultiSelectDropdownWithField';
import IconProvider from '@/components/ui/IconProvider';
import { ExportTemplate, NamingConvention } from '@/types/exportTemplates';
import { usePlatformTranslation } from '@/hooks/usePlatformTranslation';
import Editor from 'react-simple-code-editor';
import { FieldNameOverridesEditor } from './FieldNameOverridesEditor';
import { FieldExportMappingsEditor } from './FieldExportMappingsEditor';
import { extractBlueprintFields } from './utils/fieldExtractors';

interface TemplateEditorProps {
  template: ExportTemplate;
  onChange: (template: ExportTemplate) => void;
  onSave: () => void;
  onCancel: () => void;
  effectiveTheme: string;
  highlightCode: (code: string, platform: string) => string;
  templateOptions: MultiSelectOption[];
  availableTemplates: any[];
}

export const TemplateEditor = ({ 
  template, 
  onChange, 
  onSave, 
  onCancel, 
  effectiveTheme, 
  highlightCode, 
  templateOptions, 
  availableTemplates 
}: TemplateEditorProps) => {
  const { t } = usePlatformTranslation();
  const [activeEditorTab, setActiveEditorTab] = useState<'basic' | 'template' | 'object' | 'functions' | 'conventions'>('basic');
  
  const updateField = <K extends keyof ExportTemplate>(field: K, value: ExportTemplate[K]) => {
    onChange({ ...template, [field]: value });
  };
  
  return (
    <div className="border border-border rounded p-6 bg-card space-y-6">
      {/* Template Header */}
      <div className="flex items-start justify-between">
        <div>
          <h2 className="heading-4">{template.name}</h2>
          <div className="text-sm text-muted-foreground mt-1">
            {template.description || 'No description'}
          </div>
          <div className="flex gap-2 mt-2">
            <span className="text-xs px-2 py-1 bg-primary/10 text-primary rounded">
              {template.platform.toUpperCase()}
            </span>
            <span className="text-xs px-2 py-1 bg-warning/10 text-warning rounded">
              Editing
            </span>
          </div>
        </div>
        
        <div className="flex gap-2">
          <Button onClick={onCancel} variant="outline" size="sm">
            Cancel
          </Button>
          <Button onClick={onSave} size="sm" className="gap-2">
            <IconProvider iconstring="fa-check" size={12} />
            Save Changes
          </Button>
        </div>
      </div>
      
      <div className="w-full">
        <div className="flex gap-1 border-b border-border mb-4">
          {['basic', 'template', 'object', 'functions', 'conventions'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveEditorTab(tab as any)}
              className={`px-4 py-2 text-sm font-medium transition-colors capitalize ${
                activeEditorTab === tab
                  ? 'border-b-2 border-primary text-primary'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>
        
        {/* Basic Tab */}
        {activeEditorTab === 'basic' && (
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>{t('platform.settings.exporttemplatessection.template_name_label')}</Label>
              <Input
                value={template.name}
                onChange={(e) => updateField('name', e.target.value)}
                placeholder={t('platform.settings.exporttemplatessection.my_custom_template')}
              />
            </div>
            
            <div className="space-y-2">
              <Label>{t('platform.common.description')}</Label>
              <TextAreaElement
                value={template.description || ''}
                onChange={(value) => updateField('description', value)}
                placeholder={t('platform.settings.exporttemplatessection.description_of_this_template')}
                rows={3}
              />
            </div>
            
            <div className="space-y-2">
              <Label>{t('platform.settings.exporttemplatessection.platform')}</Label>
              <Select
                value={template.platform}
                onValueChange={(value) => updateField('platform', value as any)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="roblox">{t('platform.settings.exporttemplatessection.roblox')}</SelectItem>
                  <SelectItem value="cpp">C++</SelectItem>
                  <SelectItem value="unity">{t('platform.settings.exporttemplatessection.unity')}</SelectItem>
                  <SelectItem value="godot">{t('platform.settings.exporttemplatessection.godot')}</SelectItem>
                  <SelectItem value="custom">{t('platform.settings.exporttemplatessection.custom')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        )}
        
        {/* Template Tab */}
        {activeEditorTab === 'template' && (
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Filename Pattern</Label>
              <Input
                value={template.typeFile.filename}
                onChange={(e) => updateField('typeFile', { ...template.typeFile, filename: e.target.value })}
                placeholder={t('platform.settings.exporttemplatessection.templatenameluau')}
                className="font-mono text-sm"
              />
              <p className="text-xs text-muted-foreground">
                Use placeholders: {'{{'} TemplateName {'}}'}, {'{{'} ItemName {'}}'}
              </p>
            </div>
            
            <div className="space-y-2">
              <Label>Location in Package</Label>
              <Input
                value={template.typeFile.location}
                onChange={(e) => updateField('typeFile', { ...template.typeFile, location: e.target.value })}
                placeholder="types"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Template Content</Label>
              <div className="rounded-lg border border-border overflow-hidden bg-background">
                <Editor
                  value={template.typeFile.content}
                  onValueChange={(code) => updateField('typeFile', { ...template.typeFile, content: code })}
                  highlight={(code) => highlightCode(code, template.platform)}
                  padding={16}
                  style={{
                    fontFamily: '"Fira code", "Fira Mono", Consolas, Menlo, Monaco, "Courier New", monospace',
                    fontSize: '0.875rem',
                    minHeight: '400px',
                    lineHeight: '1.5',
                    backgroundColor: 'transparent',
                    color: 'var(--prism-foreground)',
                  }}
                  textareaClassName="focus:outline-none"
                  placeholder="Enter template content with placeholders..."
                />
              </div>
              <p className="text-xs text-muted-foreground">
                Use: {'{{'} TemplateName {'}}'}, {'{{'}#each fields{'}}'} ... {'{{'}fieldName{'}}'}  ... {'{{'}/ each{'}}'}
              </p>
            </div>
          </div>
        )}
        
        {/* Object Tab */}
        {activeEditorTab === 'object' && (
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>{t('platform.settings.exporttemplatessection.filename_pattern_label_2_3')}</Label>
              <Input
                value={template.instanceFile.filename}
                onChange={(e) => updateField('instanceFile', { ...template.instanceFile, filename: e.target.value })}
                placeholder={t('platform.settings.exporttemplatessection.filenameluau')}
                className="font-mono text-sm"
              />
            </div>
            
            <div className="space-y-2">
              <Label>{t('platform.settings.exporttemplatessection.location_in_package_label_2_3')}</Label>
              <Input
                value={template.instanceFile.location}
                onChange={(e) => updateField('instanceFile', { ...template.instanceFile, location: e.target.value })}
                placeholder="instances"
              />
            </div>
            
            <div className="space-y-2">
              <Label>{t('platform.settings.exporttemplatessection.template_content_label_2_3')}</Label>
              <div className="rounded-lg border border-border overflow-hidden bg-background">
                <Editor
                  value={template.instanceFile.content}
                  onValueChange={(code) => updateField('instanceFile', { ...template.instanceFile, content: code })}
                  highlight={(code) => highlightCode(code, template.platform)}
                  padding={16}
                  style={{
                    fontFamily: '"Fira code", "Fira Mono", Consolas, Menlo, Monaco, "Courier New", monospace',
                    fontSize: '0.875rem',
                    minHeight: '400px',
                    lineHeight: '1.5',
                    backgroundColor: 'transparent',
                    color: 'var(--prism-foreground)',
                  }}
                  textareaClassName="focus:outline-none"
                  placeholder={t('platform.settings.exporttemplatessection.enter_template_content_with_placeholders_placeholder')}
                />
              </div>
            </div>
          </div>
        )}
        
        {/* Functions Tab */}
        {activeEditorTab === 'functions' && (
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>{t('platform.settings.exporttemplatessection.template_functions_label')}</Label>
              <p className="text-sm text-muted-foreground">
                Define functions that will be added to each object instance. Functions can access object fields using self or this.
              </p>
              <div className="space-y-4">
                {(template.functions || []).map((func, index) => (
                  <div key={index} className="border border-border rounded p-4 space-y-4">
                    <div className="grid grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label>{t('platform.settings.exporttemplatessection.function_name')}</Label>
                        <Input
                          value={func.name}
                          onChange={(e) => {
                            const newFunctions = [...(template.functions || [])];
                            newFunctions[index] = { ...func, name: e.target.value };
                            updateField('functions', newFunctions);
                          }}
                          placeholder="functionName"
                          className="font-mono"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>{t('platform.settings.exporttemplatessection.applies_to')}</Label>
                        <Select
                          value={func.appliesTo === 'all' ? 'all' : 'selective'}
                          onValueChange={(value) => {
                            const newFunctions = [...(template.functions || [])];
                            newFunctions[index] = { 
                              ...func, 
                              appliesTo: value === 'all' ? 'all' : []
                            };
                            updateField('functions', newFunctions);
                          }}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">{t('platform.settings.exporttemplatessection.all_templates')}</SelectItem>
                            <SelectItem value="selective">{t('platform.settings.exporttemplatessection.selective_templates')}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>{t('platform.settings.exporttemplatessection.description_optional')}</Label>
                        <Input
                          value={func.description || ''}
                          onChange={(e) => {
                            const newFunctions = [...(template.functions || [])];
                            newFunctions[index] = { ...func, description: e.target.value };
                            updateField('functions', newFunctions);
                          }}
                          placeholder={t('platform.settings.exporttemplatessection.what_this_function_does')}
                        />
                      </div>
                    </div>
                    
                    {func.appliesTo === 'all' && (
                      <div className="space-y-2 p-4 border border-border rounded bg-accent/5">
                        <Label className="text-sm">{t('platform.settings.exporttemplatessection.common_variables')}</Label>
                        <p className="text-xs text-muted-foreground">
                          Select which common variables this function will use. These are available in all objects.
                        </p>
                        <MultiSelectDropdownWithField
                          value={func.commonVariables || []}
                          onChange={(value) => {
                            const newFunctions = [...(template.functions || [])];
                            newFunctions[index] = {
                              ...func,
                              commonVariables: Array.isArray(value) ? value : []
                            };
                            updateField('functions', newFunctions);
                          }}
                          options={[
                            { id: 'instanceName', label: 'instanceName', description: 'Object instance name (e.g., quest_001)' },
                            { id: 'TemplateName', label: 'TemplateName', description: 'Template/blueprint name (e.g., QuestTemplate)' },
                            { id: 'itemId', label: 'itemId', description: 'Unique object ID' },
                            { id: 'itemName', label: 'itemName', description: 'Object display name' },
                            { id: 'blueprintId', label: 'blueprintId', description: 'Template/blueprint ID' },
                            { id: 'self', label: 'self', description: 'Reference to the object itself' },
                          ]}
                          placeholder={t('platform.settings.exporttemplatessection.select_common_variables')}
                          multiple={true}
                          showSelectedOptions={true}
                        />
                      </div>
                    )}
                    
                    {func.appliesTo !== 'all' && Array.isArray(func.appliesTo) && (
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <Label>{t('platform.settings.exporttemplatessection.select_templates')}</Label>
                          <p className="text-xs text-muted-foreground">
                            This function will only be added to objects from the selected templates.
                          </p>
                          <MultiSelectDropdownWithField
                            value={func.appliesTo}
                            onChange={(value) => {
                              const newFunctions = [...(template.functions || [])];
                              const newAppliesTo = Array.isArray(value) ? value : [];
                              
                              // Keep only variables for templates that are still selected
                              const existingVariables = func.variables || [];
                              const filteredVariables = existingVariables.filter(v => 
                                newAppliesTo.includes(v.templateId)
                              );
                              
                              newFunctions[index] = { 
                                ...func, 
                                appliesTo: newAppliesTo,
                                variables: filteredVariables
                              };
                              updateField('functions', newFunctions);
                            }}
                            options={templateOptions}
                            placeholder={t('platform.settings.exporttemplatessection.select_templates_placeholder')}
                            multiple={true}
                            showSelectedOptions={true}
                          />
                        </div>
                        
                        {/* Variable selection for each selected template */}
                        {Array.isArray(func.appliesTo) && func.appliesTo.length > 0 && (
                          <div className="space-y-3 p-4 border border-border rounded bg-accent/5">
                            <Label className="text-sm">{t('platform.settings.exporttemplatessection.template_variables')}</Label>
                            <p className="text-xs text-muted-foreground">
                              Select which fields from each template this function will use. These will be available as {'{{fieldName}}'}.
                            </p>
                            {func.appliesTo.map((templateId) => {
                              const selectedTemplate = availableTemplates.find(t => t.id === templateId);
                              if (!selectedTemplate) return null;
                              
                              // Get fields from the template's data structure
                              const templateFields = selectedTemplate.data ? extractBlueprintFields(selectedTemplate.data) : [];
                              
                              const fieldOptions: MultiSelectOption[] = templateFields.map(fieldName => ({
                                id: fieldName,
                                label: fieldName
                              }));
                              
                              const templateVar = (func.variables || []).find(v => v.templateId === templateId);
                              const selectedFields = templateVar?.fieldNames || [];
                              
                              return (
                                <div key={templateId} className="space-y-2 p-3 border border-border/50 rounded bg-background">
                                  <div className="text-sm font-medium">{selectedTemplate.name}</div>
                                  <MultiSelectDropdownWithField
                                    value={selectedFields}
                                    onChange={(value) => {
                                      const newFunctions = [...(template.functions || [])];
                                      const existingVariables = func.variables || [];
                                      const otherVariables = existingVariables.filter(v => v.templateId !== templateId);
                                      
                                      const newVariables = [
                                        ...otherVariables,
                                        {
                                          templateId,
                                          fieldNames: Array.isArray(value) ? value : []
                                        }
                                      ].filter(v => v.fieldNames.length > 0);
                                      
                                      newFunctions[index] = {
                                        ...func,
                                        variables: newVariables
                                      };
                                      updateField('functions', newFunctions);
                                    }}
                                    options={fieldOptions}
                                    placeholder={fieldOptions.length > 0 ? "Select fields..." : "No fields available"}
                                    multiple={true}
                                    showSelectedOptions={true}
                                  />
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}
                    
                    <div className="space-y-2">
                      <Label>{t('platform.settings.exporttemplatessection.function_code')}</Label>
                      <div className="rounded-lg border border-border overflow-hidden bg-background">
                        <Editor
                          value={func.content}
                          onValueChange={(code) => {
                            const newFunctions = [...(template.functions || [])];
                            newFunctions[index] = { ...func, content: code };
                            updateField('functions', newFunctions);
                          }}
                          highlight={(code) => highlightCode(code, template.platform)}
                          padding={16}
                          style={{
                            fontFamily: '"Fira code", "Fira Mono", Consolas, Menlo, Monaco, "Courier New", monospace',
                            fontSize: '0.875rem',
                            minHeight: '200px',
                            lineHeight: '1.5',
                            backgroundColor: 'transparent',
                            color: 'var(--prism-foreground)',
                          }}
                          textareaClassName="focus:outline-none"
                          placeholder={t('platform.settings.exporttemplatessection.function_code_here')}
                        />
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Access fields using self.fieldName (Lua/Python) or this.fieldName (C#/C++/TypeScript)
                      </p>
                    </div>
                    
                    <div className="flex justify-end">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          const newFunctions = (template.functions || []).filter((_, i) => i !== index);
                          updateField('functions', newFunctions);
                        }}
                        className="text-destructive hover:text-destructive"
                      >
                        Remove Function
                      </Button>
                    </div>
                  </div>
                ))}
                
                <Button
                  variant="outline"
                  onClick={() => {
                    const newFunctions: typeof template.functions = [
                      ...(template.functions || []),
                      { name: 'newFunction', description: '', content: '', appliesTo: 'all' }
                    ];
                    updateField('functions', newFunctions);
                  }}
                  className="w-full"
                >
                  <IconProvider iconstring="fa-plus" size={12} className="mr-2" />
                  Add Function
                </Button>
              </div>
            </div>
          </div>
        )}
        
        {/* Conventions Tab */}
        {activeEditorTab === 'conventions' && (
          <div className="space-y-4 mt-4">
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t('platform.settings.exporttemplatessection.template_name_convention')}</Label>
                  <Select
                    value={template.namingConventions.templateName}
                    onValueChange={(value: NamingConvention) =>
                      updateField('namingConventions', {
                        ...template.namingConventions,
                        templateName: value
                      })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="PascalCase">PascalCase</SelectItem>
                      <SelectItem value="camelCase">camelCase</SelectItem>
                      <SelectItem value="snake_case">snake_case</SelectItem>
                      <SelectItem value="SCREAMING_SNAKE_CASE">SCREAMING_SNAKE_CASE</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              
                <div className="space-y-2">
                  <Label>{t('platform.settings.exporttemplatessection.field_name_convention')}</Label>
                  <Select
                    value={template.namingConventions.fieldName}
                    onValueChange={(value: NamingConvention) =>
                      updateField('namingConventions', {
                        ...template.namingConventions,
                        fieldName: value
                      })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="PascalCase">PascalCase</SelectItem>
                      <SelectItem value="camelCase">camelCase</SelectItem>
                      <SelectItem value="snake_case">snake_case</SelectItem>
                      <SelectItem value="SCREAMING_SNAKE_CASE">SCREAMING_SNAKE_CASE</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label>{t('platform.settings.exporttemplatessection.instance_name_convention')}</Label>
                  <Select
                    value={template.namingConventions.instanceName}
                    onValueChange={(value: NamingConvention) =>
                      updateField('namingConventions', {
                        ...template.namingConventions,
                        instanceName: value
                      })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="PascalCase">PascalCase</SelectItem>
                      <SelectItem value="camelCase">camelCase</SelectItem>
                      <SelectItem value="snake_case">snake_case</SelectItem>
                      <SelectItem value="SCREAMING_SNAKE_CASE">SCREAMING_SNAKE_CASE</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label>{t('platform.settings.exporttemplatessection.file_name_convention')}</Label>
                  <Select
                    value={template.namingConventions.fileName}
                    onValueChange={(value: NamingConvention) =>
                      updateField('namingConventions', {
                        ...template.namingConventions,
                        fileName: value
                      })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="PascalCase">PascalCase</SelectItem>
                      <SelectItem value="camelCase">camelCase</SelectItem>
                      <SelectItem value="snake_case">snake_case</SelectItem>
                      <SelectItem value="SCREAMING_SNAKE_CASE">SCREAMING_SNAKE_CASE</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <FieldNameOverridesEditor 
                template={template}
                onChange={onChange}
                availableTemplates={availableTemplates}
              />
              
              <FieldExportMappingsEditor 
                template={template}
                onChange={onChange}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
